/** The invite actions: the menu (the grid row's ⋮ and the drawer's Actions button) and the page's
 *  runner — the requests, the confirmations and the small dialogs behind them.
 *
 *  The runner lives on the page and renders its dialogs there, as siblings of the detail drawer
 *  (a dialog opened from inside a drawer is aria-hidden — lessons). Both the grid and the drawer
 *  call its `run`.
 *
 *  The link is fetched from `GET …/link/` only when the operator copies it or opens its QR code
 *  (audited per call), straight from the handler: never cached (no query, no mutation cache) and
 *  never kept after the popover closes. */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Button, IconButton, ListItemText, Menu, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import * as invitesApi from '@/api/onboardingInvites';
import type { InviteListItem } from '@/api/onboardingInvites';
import { AppDialog } from '@/components/AppDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { QrCodePopover } from '@/components/QrCodePopover';
import { useSnackbar } from '@/components/Snackbar';

import { InviteFormDialog, type RestechOption } from './InviteFormDialog';
import {
  inviteActionLabel,
  inviteActionsFor,
  tenantOnboardingPath,
  type InviteActionId,
  type InviteActionPerms,
} from './inviteActionRules';

export type RunInviteAction = (action: InviteActionId, invite: InviteListItem, anchor: HTMLElement | null) => void;

/** The invite's actions as a menu: an icon button in a grid row, a labelled button in the drawer.
 *  Renders nothing when the operator may do nothing with it. */
export function InviteActionsMenu({
  invite,
  perms,
  onRun,
  variant = 'icon',
}: {
  invite: InviteListItem;
  perms: InviteActionPerms;
  onRun: RunInviteAction;
  variant?: 'icon' | 'button';
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const actions = inviteActionsFor(invite, perms);
  if (actions.length === 0) return null;
  const open = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // never the grid's row click
    setAnchor(e.currentTarget);
  };
  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title="Actions">
          <IconButton
            ref={trigger}
            size="small"
            aria-label={`Actions for invite ${invite.public_id}`}
            aria-haspopup="menu"
            onClick={open}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button ref={trigger} size="small" variant="outlined" endIcon={<ArrowDropDownIcon />} aria-haspopup="menu" onClick={open}>
          Actions
        </Button>
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        // React events bubble through portals: keep menu clicks (and the backdrop's) off the row.
        onClick={(e) => e.stopPropagation()}
      >
        {actions.map((action) => (
          <MenuItem
            key={action}
            onClick={() => {
              setAnchor(null);
              onRun(action, invite, trigger.current);
            }}
            sx={action === 'revoke' ? { color: 'error.main' } : undefined}
          >
            <ListItemText>{inviteActionLabel(action, invite)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

type SimpleAction = 'send' | 'rotate' | 'reinstate' | 'release';

const DONE: Record<SimpleAction, string> = {
  send: 'E-mail sent.',
  rotate: 'Code rotated — the old link no longer works.',
  reinstate: 'Invite reinstated.',
  release: 'Claim released.',
};

const FAILED: Record<SimpleAction | 'revoke' | 'expiry', string> = {
  send: 'The e-mail could not be sent.',
  rotate: 'The code could not be rotated.',
  reinstate: 'The invite could not be reinstated.',
  release: 'The claim could not be released.',
  revoke: 'The invite could not be revoked.',
  expiry: 'The expiry could not be changed.',
};

/** The page's action runner: `run` for the grid and the drawer, `elements` to render once. */
export function useInviteActionRunner({ restechOptions }: { restechOptions: RestechOption[] | null }) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [qr, setQr] = useState<{ invite: InviteListItem; link: string; anchor: HTMLElement } | null>(null);
  const [confirm, setConfirm] = useState<{ action: 'rotate' | 'release'; invite: InviteListItem } | null>(null);
  const [revoking, setRevoking] = useState<InviteListItem | null>(null);
  const [expiry, setExpiry] = useState<InviteListItem | null>(null);
  const [editing, setEditing] = useState<InviteListItem | null>(null);
  // The newest link request wins: a slow answer for an earlier click is dropped.
  const linkSeq = useRef(0);

  const invalidate = () => void qc.invalidateQueries({ queryKey: invitesApi.invitesKeys.all });

  const simple = useMutation({
    mutationFn: ({ action, id }: { action: SimpleAction; id: number }) => {
      switch (action) {
        case 'send':
          return invitesApi.send(id);
        case 'rotate':
          return invitesApi.rotate(id);
        case 'reinstate':
          return invitesApi.reinstate(id);
        case 'release':
          return invitesApi.release(id);
      }
    },
    onSuccess: (detail, { action }) =>
      snackbar.show(action === 'send' && detail.email ? `E-mail sent to ${detail.email}.` : DONE[action], 'success'),
    onError: (e, { action }) => snackbar.show(invitesApi.inviteErrorMessage(e, FAILED[action]), 'error'),
    // A refusal (409) means the row moved on meanwhile: show it as it is now.
    onSettled: invalidate,
  });

  const fetchLink = async (invite: InviteListItem): Promise<string | null> => {
    const seq = ++linkSeq.current;
    try {
      const { link } = await invitesApi.link(invite.id);
      return seq === linkSeq.current ? link : null;
    } catch (e) {
      if (seq === linkSeq.current) snackbar.show(invitesApi.inviteErrorMessage(e, 'The link could not be shown.'), 'error');
      return null;
    }
  };

  const runNow: RunInviteAction = (action, invite, anchor) => {
    switch (action) {
      case 'copy':
        void fetchLink(invite).then((link) => {
          if (!link) return;
          void navigator.clipboard
            ?.writeText(link)
            .then(() => snackbar.show('Link copied.', 'success'))
            .catch(() => snackbar.show('Copy failed — open the QR code to see the link.', 'error'));
        });
        return;
      case 'qr':
        void fetchLink(invite).then((link) => {
          if (link && anchor?.isConnected) setQr({ invite, link, anchor });
          else if (link) snackbar.show('The row moved — open the QR code again.', 'info');
        });
        return;
      case 'send':
      case 'reinstate':
        simple.mutate({ action, id: invite.id });
        return;
      case 'rotate':
      case 'release':
        setConfirm({ action, invite });
        return;
      case 'revoke':
        setRevoking(invite);
        return;
      case 'edit':
        setEditing(invite);
        return;
      case 'expiry':
        setExpiry(invite);
        return;
      case 'open_onboarding':
        if (invite.tenant) navigate(tenantOnboardingPath(invite.tenant.id));
        return;
    }
  };

  // A stable `run` for the grid's memoised columns; it calls this render's handler.
  const latest = useRef(runNow);
  latest.current = runNow;
  const run = useCallback<RunInviteAction>((...args) => latest.current(...args), []);

  const elements = (
    <>
      <QrCodePopover
        value={qr?.link ?? null}
        anchorEl={qr?.anchor ?? null}
        onClose={() => setQr(null)}
        title={qr ? `QR code — ${qr.invite.label}` : ''}
        fileName={qr ? `invite-${qr.invite.public_id}.png` : 'invite.png'}
      />
      <ConfirmDialog
        open={confirm?.action === 'rotate'}
        title="Rotate the code?"
        message="The current link — and every printed QR code of it — stops working at once. A browser that already started keeps its claim. Copy or print the new link afterwards."
        confirmLabel="Rotate code"
        destructive
        onConfirm={() => {
          if (confirm) simple.mutate({ action: 'rotate', id: confirm.invite.id });
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm?.action === 'release'}
        title="Release the claim?"
        message="The browser holding the link loses it at once and the choices it made so far are cleared. Any browser can then start again with the link."
        confirmLabel="Release claim"
        destructive
        onConfirm={() => {
          if (confirm) simple.mutate({ action: 'release', id: confirm.invite.id });
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
      <RevokeDialog invite={revoking} onClose={() => setRevoking(null)} onDone={invalidate} />
      <ExpiryDialog invite={expiry} onClose={() => setExpiry(null)} onDone={invalidate} />
      <InviteFormDialog
        open={Boolean(editing)}
        invite={editing}
        restechOptions={restechOptions}
        onClose={() => setEditing(null)}
      />
    </>
  );

  return { run, elements, pending: simple.isPending };
}

// ---------------------------------------------------------------------------------------------

const RevokeSchema = z.object({ reason: z.string().max(2000, 'At most 2000 characters.') });

function RevokeDialog({ invite, onClose, onDone }: { invite: InviteListItem | null; onClose: () => void; onDone: () => void }) {
  const snackbar = useSnackbar();
  const { register, handleSubmit, reset, formState } = useForm<{ reason: string }>({
    resolver: zodResolver(RevokeSchema),
    defaultValues: { reason: '' },
  });
  useEffect(() => {
    if (invite) reset({ reason: '' });
  }, [invite, reset]);
  const revoke = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => invitesApi.revoke(id, reason),
    onSuccess: () => {
      snackbar.show('Invite revoked — the link no longer works.', 'success');
      onClose();
    },
    onError: (e) => snackbar.show(invitesApi.inviteErrorMessage(e, FAILED.revoke), 'error'),
    onSettled: onDone,
  });
  const submit = handleSubmit((v) => invite && revoke.mutate({ id: invite.id, reason: v.reason.trim() }));
  return (
    <AppDialog
      open={Boolean(invite)}
      onClose={onClose}
      title={`Revoke invite “${invite?.label ?? ''}”?`}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button color="error" variant="contained" disabled={revoke.isPending} onClick={() => void submit()}>
            Revoke
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <span>
          The link stops working at once{invite?.claim.claimed ? ', and the browser holding it loses its session' : ''}.
          Reinstate makes it available again.
        </span>
        <TextField
          {...register('reason')}
          size="small"
          label="Reason (optional)"
          multiline
          minRows={2}
          error={Boolean(formState.errors.reason)}
          helperText={formState.errors.reason?.message ?? 'Kept with the invite and in its timeline.'}
        />
      </Stack>
    </AppDialog>
  );
}

const ExpirySchema = z.object({
  expires: z
    .date()
    .nullable()
    .refine((d) => d === null || !Number.isNaN(d.getTime()), 'A date and a time.'),
});

function ExpiryDialog({ invite, onClose, onDone }: { invite: InviteListItem | null; onClose: () => void; onDone: () => void }) {
  const snackbar = useSnackbar();
  const { control, handleSubmit, reset, setValue, formState } = useForm<{ expires: Date | null }>({
    resolver: zodResolver(ExpirySchema),
    defaultValues: { expires: null },
  });
  useEffect(() => {
    if (invite) reset({ expires: invite.expires_ts ? new Date(invite.expires_ts) : null });
  }, [invite, reset]);
  const save = useMutation({
    mutationFn: ({ id, expires }: { id: number; expires: Date | null }) =>
      invitesApi.update(id, { expires_ts: expires ? expires.toISOString() : null }),
    onSuccess: (detail) => {
      snackbar.show(detail.expires_ts ? 'Expiry changed.' : 'The invite no longer expires.', 'success');
      onClose();
    },
    onError: (e) => snackbar.show(invitesApi.inviteErrorMessage(e, FAILED.expiry), 'error'),
    onSettled: onDone,
  });
  const submit = handleSubmit((v) => invite && save.mutate({ id: invite.id, expires: v.expires }));
  return (
    <AppDialog
      open={Boolean(invite)}
      onClose={onClose}
      title={invite?.status === 'expired' ? 'Extend expiry' : 'Change expiry'}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={() => setValue('expires', null, { shouldDirty: true })}>No expiry</Button>
          <Button variant="contained" disabled={save.isPending} onClick={() => void submit()}>
            Save
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Controller
          control={control}
          name="expires"
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Expires"
                value={field.value}
                onChange={(d) => field.onChange(d)}
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: Boolean(formState.errors.expires),
                    helperText:
                      formState.errors.expires?.message ??
                      'Your local time. A date in the future brings an expired invite back; empty: it never expires.',
                  },
                }}
              />
            </LocalizationProvider>
          )}
        />
      </Stack>
    </AppDialog>
  );
}
